/* eslint-disable no-unused-expressions */
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { API_TOKEN, API_URL } from "../../Config/secrets";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  allOrganizations: {},
  totalOrganizations: 0,
  planRequests: {},
  totalPlanRequests: 0,
  pittiOrganizations: {},
  totalPittiOrganizations: 0,
  discountCoupons: {},
  totalDiscountCoupons: 0,
  referralCodes: {},
  totalReferralCodes: 0,
  engagements: {},
  totalEngagements: {},
  salesAgentProfiles: {},
  salesAgentProfileItems: { brand: [], retailer: [] },
  totalSalesAgentProfiles: {},
  salesAgentProfilesLoading: false,
  filterData: null,
  filterLoading: false,
  filters: {},
};

const slice = createSlice({
  name: "organizationManagement",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getAllOrganizationsSuccess(state, action) {
      state.error = null;
      state.totalOrganizations = action.payload.total;
      state.allOrganizations = {
        ...state.allOrganizations,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    getPlanRequestsSuccess(state, action) {
      state.error = null;
      state.totalPlanRequests = action.payload.total;
      state.planRequests = {
        ...state.planRequests,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    getPittiOrganizationsSuccess(state, action) {
      state.error = null;
      state.totalPittiOrganizations = action.payload.total;
      state.pittiOrganizations = {
        ...state.pittiOrganizations,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    getDiscountCouponsSuccess(state, action) {
      state.error = null;
      state.totalDiscountCoupons = action.payload.total;
      state.discountCoupons = {
        ...state.discountCoupons,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    getReferralCodesSuccess(state, action) {
      state.error = null;
      state.totalReferralCodes = action.payload.total;
      state.referralCodes = {
        ...state.referralCodes,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    getOrganizationEngagementsSuccess(state, action) {
      state.error = null;
      const { tab, page, total, data } = action.payload;

      // Initialize tab if it doesn't exist
      if (!state.engagements[tab]) {
        state.engagements[tab] = {};
      }

      // Store data for this tab and page
      state.engagements[tab][page] = data;

      // Store total count for this tab
      state.totalEngagements[tab] = total;

      state.isLoading = false;
    },
    startSalesAgentProfilesLoading(state) {
      state.salesAgentProfilesLoading = true;
    },
    stopSalesAgentProfilesLoading(state) {
      state.salesAgentProfilesLoading = false;
    },
    getSalesAgentProfilesSuccess(state, action) {
      state.error = null;
      const { profileType, page, total, data, append } = action.payload;

      if (!state.salesAgentProfiles[profileType]) {
        state.salesAgentProfiles[profileType] = {};
      }

      state.salesAgentProfiles[profileType][page] = data;
      state.totalSalesAgentProfiles[profileType] = total;

      if (append) {
        state.salesAgentProfileItems[profileType] = [
          ...(state.salesAgentProfileItems[profileType] || []),
          ...data,
        ];
      } else {
        state.salesAgentProfileItems[profileType] = data;
      }

      state.salesAgentProfilesLoading = false;
    },
    getOrganizationFiltersSuccess(state, action) {
      state.filterData = action.payload;
      state.filterLoading = false;
      state.error = null;
    },
    setFilters(state, action) {
      state.filters = action.payload;
    },
    resetOrganizationManagement: () => initialState,
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetOrganizationManagement, setFilters } = slice.actions;

// ----------------------------------------------------------------------

const header = {
  Authorization: API_TOKEN,
};

export function getAllOrganizations(page = 1, query = '', take = 10, filters = {}, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take, filters };

      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/organization-managment/get-organizations`, payload, {
        headers: header,
      });

      console.log('GET ORGANIZATIONS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getAllOrganizationsSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data?.data?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch organizations", { autoClose: 3000 });
      }
    } catch (error) {
      console.error('GET ORGANIZATIONS ERROR', error);
      toast.error(error?.message || "Failed to fetch organizations", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getOrganizationFilters() {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/get-organization-filters`, {}, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.getOrganizationFiltersSuccess(response.data.data));
        return response.data.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch filters", { autoClose: 3000 });
      }
    } catch (error) {
      console.error('GET ORGANIZATION FILTERS ERROR', error);
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getPittiOrganizations(page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take };

      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/organization-managment/get-pitti-organizations`, payload, {
        headers: header,
      });

      console.log('GET PITTI ORGANIZATIONS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getPittiOrganizationsSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data?.data?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch Pitti organizations", { autoClose: 3000 });
      }
    } catch (error) {
      console.error('GET PITTI ORGANIZATIONS ERROR', error);
      toast.error(error?.message || "Failed to fetch Pitti organizations", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getDiscountCoupons(page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take };
      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/discount-coupon/list`, payload, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getDiscountCouponsSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to fetch discount coupons", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch discount coupons", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function createDiscountCoupon(payload) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/discount-coupon/create`, payload, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Coupon created successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to create coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getReferralCodes(page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take };
      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/organization-managment/get-referral-codes`, payload, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getReferralCodesSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to fetch referral codes", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch referral codes", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function createReferralCode(payload) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/create-referral`, payload, {
        headers: header,
      });

      if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Referral code created successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to create referral code", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create referral code", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function deleteReferralCodes(ids) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/delete-referral-codes`, { ids }, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Referral codes deleted successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to delete referral codes", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete referral codes", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function removeOrganizationReferral(organizationId) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/remove-organization-referral`, { organizationId }, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization referral removed successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to remove organization referral", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to remove organization referral", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function sendInviteEmail(organizationId) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/send-invite-email`, { organization_id: organizationId }, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Invite email sent successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to send invite email", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to send invite email", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function assignDiscountCoupon(couponId, organizationId, expiresAt = null) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/discount-coupon/assign-to-organization`,
        {
          coupon_id: Number(couponId),
          organization_id: Number(organizationId),
          expires_at: expiresAt || null,
        },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Coupon assigned successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to assign coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to assign coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function unassignDiscountCoupon(couponId, organizationId = null) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/discount-coupon/unassign-from-organization`,
        {
          coupon_id: couponId ? Number(couponId) : null,
          organization_id: organizationId ? Number(organizationId) : null,
        },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Coupon unassigned successfully", { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || "Failed to unassign coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to unassign coupon", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getOrganizationDropdownOptions(page = 1, query = '', take = 20) {
  return async (dispatch) => {
    try {
      const payload = { skip: page, take };
      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/organization-managment/get-organizations-dropdown`, payload, {
        headers: header,
      });

      if (response?.data?.statusCode === 200) {
        return {
          data: response?.data?.data?.data || [],
          count: response?.data?.data?.count || 0,
        };
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      return { data: [], count: 0 };
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data));
      return { data: [], count: 0 };
    }
  };
}

export function getOrganizationMetrics(organizationId) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/organization-managment/get-organization-metrics`,
        { organization_id: Number(organizationId) },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        return response?.data?.data;
      }

      toast.error(response?.data?.message || "Failed to fetch organization metrics", { autoClose: 3000 });
      return null;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch organization metrics", { autoClose: 3000 });
      return null;
    }
  };
}

export function exportOrganizations(query = '', filters = {}) {
  return async () => {
    try {
      const payload = { filters };
      if (query) payload.query = query;

      const response = await axios.post(
        `${API_URL}/organization-managment/export-organizations`,
        payload,
        {
          headers: { ...header, Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
          responseType: 'blob',
        }
      );

      // Trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `organizations_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export downloaded successfully", { autoClose: 3000 });
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to export organizations", { autoClose: 3000 });
      return false;
    }
  };
}

export function toggleOrganizationStatus(organizationId, currentStatus) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/toggle-organization-users`,
        { organizationId, active: !currentStatus },
        { headers: header }
      );

      console.log('TOGGLE ORGANIZATION STATUS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization status updated", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update organization status", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE ORGANIZATION STATUS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update organization status", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function addContactCredit(organizationId, credits) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/add-contact-credits`,
        { organizationId, contactCredits: credits },
        { headers: header }
      );

      console.log('ADD CONTACT CREDITS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Contact credits added successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to add contact credits", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('ADD CONTACT CREDITS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to add contact credits", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function addMatchCredits(organizationId, matchCredits, creditMode = 'add') {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/add-credits`,
        { organization_id: organizationId, credits: matchCredits, mode: creditMode },
        { headers: header }
      );

      console.log('ADD MATCH CREDITS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Match credits added successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to add match credits", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('ADD MATCH CREDITS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to add match credits", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function deleteOrganizations(organizationIds) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/delete-organization`,
        { organizationIds },
        { headers: header }
      );

      console.log('DELETE ORGANIZATIONS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization(s) deleted successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to delete organization(s)", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('DELETE ORGANIZATIONS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete organization(s)", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function updateOrganizationSeats(organizationId, seats) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/update-seats`,
        { organizationId, seats },
        { headers: header }
      );

      console.log('UPDATE ORGANIZATION SEATS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Allowed seats updated successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update allowed seats", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('UPDATE ORGANIZATION SEATS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update allowed seats", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

// existingPlanAction ('transfer' | 'fresh'): what happens to an active plan's credits (Seasonal-Pass.md D2)
export function subscribeOrganizationPittiPlan(organizationId, planId, existingPlanAction) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/subscribe-or-renew-organization-pitti-plan`,
        { organization_id: organizationId, plan_id: planId, existing_plan_action: existingPlanAction },
        { headers: header }
      );

      console.log('SUBSCRIBE ORGANIZATION PLAN RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization subscribed to plan successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to subscribe to plan", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('SUBSCRIBE ORGANIZATION PLAN ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to subscribe to plan", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function subscribeHyperscoutPlan(organizationId, planId, existingPlanAction) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/subscribe-or-renew-organization-hyperscout-plan`,
        {
          organization_id: Number(organizationId),
          plan_id: Number(planId),
          existing_plan_action: existingPlanAction,
        },
        { headers: header }
      );

      console.log('SUBSCRIBE HYPERSCOUT PLAN RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization subscribed to Hyperscout plan successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to subscribe to Hyperscout plan", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('SUBSCRIBE HYPERSCOUT PLAN ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to subscribe to Hyperscout plan", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function subscribeCustomHyperscoutPlan(organizationId, customPlanData, existingPlanAction) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/subscribe-or-renew-organization-custom-plan`,
        {
          organization_id: Number(organizationId),
          plan_type: customPlanData.plan_type,
          credits: Number(customPlanData.credits),
          existing_plan_action: existingPlanAction,
        },
        { headers: header }
      );

      console.log('SUBSCRIBE CUSTOM HYPERSCOUT PLAN RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization subscribed to custom plan successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to subscribe to custom plan", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('SUBSCRIBE CUSTOM HYPERSCOUT PLAN ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to subscribe to custom plan", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

// Seasonal Pass payment link (Seasonal-Pass.md): the admin copies the URL and sends it. payload:
// { email?, organization_id?, existing_plan_action? }; the signed-in admin's name is logged with it.
export function createPassLink(payload) {
  return async (dispatch, getState) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/create-pass-link`,
        { ...payload, admin_name: getState()?.auth?.user?.name },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Payment link created", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to create the payment link", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('CREATE PASS LINK ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to create the payment link", { autoClose: 3000 });
      return error?.response?.data;
    }
  };
}

export function getPassLinks(page = 1, query = '', take = 10) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/get-pass-links`,
        { skip: page, take, ...(query && { query }) },
        { headers: header }
      );
      return response?.data;
    } catch (error) {
      console.error('GET PASS LINKS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch payment links", { autoClose: 3000 });
      return error?.response?.data;
    }
  };
}

export function toggleSubscription(organizationId, isPaused) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/toggle-subsciption`,
        {
          organization_id: Number(organizationId),
          toggle: isPaused
        },
        { headers: header }
      );

      console.log('TOGGLE SUBSCRIPTION RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Subscription status updated successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update subscription status", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE SUBSCRIPTION ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update subscription status", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function toggleOrganizationBrandSetupFlow(organizationId, currentToggleState) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/toggle-organization-brand-setup-flow`,
        { organization_id: organizationId, toggle: !currentToggleState },
        { headers: header }
      );

      console.log('TOGGLE BRAND SETUP FLOW RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Brand setup flow updated", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update brand setup flow", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE BRAND SETUP FLOW ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update brand setup flow", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function activateTrial(organizationId, trialPeriod, existingPlanAction) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/activate-trial`,
        {
          organization_id: organizationId,
          trial_period: trialPeriod,
          existing_plan_action: existingPlanAction,
        },
        { headers: header }
      );

      console.log('ACTIVATE TRIAL RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Trial activated successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to activate trial", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('ACTIVATE TRIAL ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to activate trial", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getPlanRequests(page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take };

      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/organization-managment/get-plan-requests`, payload, {
        headers: header,
      });

      console.log('GET PLAN REQUESTS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getPlanRequestsSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data?.data?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch plan requests", { autoClose: 3000 });
      }
    } catch (error) {
      console.error('GET PLAN REQUESTS ERROR', error);
      toast.error(error?.message || "Failed to fetch plan requests", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function approvePlanRequest(organization_id, id) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/approve-plan-request`,
        { organization_id, id },
        { headers: header }
      );

      console.log('APPROVE PLAN REQUEST RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Plan request approved successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to approve plan request", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('APPROVE PLAN REQUEST ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to approve plan request", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function cancelSubscription(organizationId) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/cancel-subscription`,
        { organization_id: Number(organizationId) },
        { headers: header }
      );

      console.log('CANCEL SUBSCRIPTION RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Subscription cancelled successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to cancel subscription", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('CANCEL SUBSCRIPTION ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to cancel subscription", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function checkOrganizationExist(id, type) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/organization-managment/check-organization-exist`,
        { id: Number(id), type },
        { headers: header }
      );

      console.log('CHECK ORGANIZATION EXIST RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('CHECK ORGANIZATION EXIST ERROR', error);
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function setupPittiBrandRetailerAccount(data) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/auth/setup-pitti-brand-retailer-account`,
        data,
        { headers: header }
      );

      console.log('SETUP PITTI ACCOUNT RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Account setup successful", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to setup account", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('SETUP PITTI ACCOUNT ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to setup account", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function setupPittiBrandRetailerAccountBulk(accounts) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/setup-pitti-brand-retailer-account-bulk`,
        { accounts },
        { headers: header }
      );

      console.log('SETUP PITTI ACCOUNT BULK RESPONSE', response.data);

      const statusCode = response?.data?.statusCode;
      const data = response?.data?.data;

      if (statusCode === 200) {
        // Synchronous result — check for partial failures
        if (data?.failed > 0) {
          toast.warning(
            `Bulk setup completed: ${data.successful} succeeded, ${data.failed} failed`,
            { autoClose: 5000 }
          );
        } else {
          toast.success(response?.data?.message || "Bulk account setup completed successfully", { autoClose: 3000 });
        }
        return response?.data;
      } else if (statusCode === 202) {
        toast.success(response?.data?.message || "Bulk account setup is being processed in the background", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to setup bulk accounts", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('SETUP PITTI ACCOUNT BULK ERROR', error);
      const errData = error?.response?.data;
      const statusCode = errData?.statusCode || error?.response?.status;
      const message = errData?.message || error?.message || "Failed to setup bulk accounts";

      if (statusCode === 400) {
        toast.error(message, { autoClose: 5000 });
      } else if (statusCode === 500) {
        toast.error("Server error: " + message, { autoClose: 5000 });
      } else {
        toast.error(message, { autoClose: 3000 });
      }

      dispatch(slice.actions.hasError(errData));
      return { statusCode: statusCode || 500, message, error: true };
    }
  };
}

export function resetOrganizationAccount(organizationIds) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/organization-managment/reset-organization-account`,
        { organizationIds },
        { headers: header }
      );

      console.log('RESET ORGANIZATION ACCOUNT RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization account(s) reset successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to reset organization account(s)", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('RESET ORGANIZATION ACCOUNT ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to reset organization account(s)", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function resetOrganizationPassword(organizationId, newPassword) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/organization-managment/organization-reset-password`,
        {
          organization_id: Number(organizationId),
          new_password: newPassword,
        },
        { headers: header }
      );

      console.log('RESET ORGANIZATION PASSWORD RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Organization password reset successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to reset organization password", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('RESET ORGANIZATION PASSWORD ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to reset organization password", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getOrganizationEngagements(organizationId, tab, page = 1, take = 10) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const payload = {
        organization_id: Number(organizationId),
        tab: tab.toLowerCase(),
        skip: page,
        take,
      };

      const response = await axios.post(
        `${API_URL}/organization-managment/get-organization-engagements`,
        payload,
        { headers: header }
      );

      console.log('GET ORGANIZATION ENGAGEMENTS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getOrganizationEngagementsSuccess({
            tab,
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch organization engagements", { autoClose: 3000 });
        return response?.data;
      }
    } catch (error) {
      console.error('GET ORGANIZATION ENGAGEMENTS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch organization engagements", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function getSalesAgentProfiles(organizationId, profileType, page = 1, take = 40, query = '', append = false) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startSalesAgentProfilesLoading());

      const response = await axios.post(
        `${API_URL}/organization-managment/get-sales-agent-profiles`,
        {
          organization_id: Number(organizationId),
          profile_type: profileType,
          skip: page,
          take,
          query,
        },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getSalesAgentProfilesSuccess({
            profileType,
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
            append,
          })
        );
        return response?.data;
      }

      dispatch(slice.actions.stopSalesAgentProfilesLoading());
      toast.error(response?.data?.message || 'Failed to fetch sales agent profiles', { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      dispatch(slice.actions.stopSalesAgentProfilesLoading());
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch sales agent profiles', { autoClose: 3000 });
      return error?.response?.data;
    }
  };
}

export function addSalesAgentProfile(organizationId, profileIds, profileType) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/organization-managment/add-sales-agent-profile`,
        {
          organization_id: Number(organizationId),
          profile_ids: profileIds,
          profile_type: profileType,
        },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || 'Profiles added successfully', { autoClose: 3000 });
        return response?.data;
      }

      const errorMessage = response?.data?.message || 'Failed to add profiles';
      toast.error(errorMessage, { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add profiles';
      const displayMessage = Array.isArray(errorMessage)
        ? errorMessage.join(', ')
        : errorMessage;
      toast.error(displayMessage, { autoClose: 3000 });
      return error?.response?.data;
    }
  };
}

export function deleteSalesAgentConnectedProfiles(organizationId, profileIds, profileType) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/organization-managment/delete-sales-agent-connected-profiles`,
        {
          organization_id: Number(organizationId),
          profile_ids: profileIds,
          profile_type: profileType,
        },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || 'Profiles removed successfully', { autoClose: 3000 });
        return response?.data;
      }

      toast.error(response?.data?.message || 'Failed to remove profiles', { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to remove profiles', { autoClose: 3000 });
      return error?.response?.data;
    }
  };
}
